/**
 * Helpers for the daily-quest archive (`dailyQuestHistory` slice on the
 * progress store). The archive is a rolling map of ISO-date → frozen
 * quest slice, used by the calendar make-up flow to show "what were
 * the quests on this day" and let the child still finish them.
 */
import { DAILY_QUEST_HISTORY_DAYS } from './progressMigration.js';
import { buildDailyQuestSlice } from './dailyQuests.js';

/**
 * Keep only the most-recent N keys of the archive. ISO dates sort
 * lexicographically the same as chronologically, so a plain sort() does
 * the right thing.
 */
export function pruneQuestArchive(
  archive,
  cap = DAILY_QUEST_HISTORY_DAYS
) {
  if (!archive || typeof archive !== 'object') return {};
  const keys = Object.keys(archive).sort();
  if (keys.length <= cap) return archive;
  const keep = keys.slice(-cap);
  const next = {};
  for (const k of keep) next[k] = archive[k];
  return next;
}

/**
 * Push `oldSlice` into the archive keyed by its date, then prune.
 * Returns the same reference when the slice can't be archived
 * (no date, no quests), so React/Zustand doesn't trip a needless
 * re-render.
 */
export function archiveQuestSlice(currentArchive, oldSlice) {
  if (
    !oldSlice ||
    typeof oldSlice.date !== 'string' ||
    !Array.isArray(oldSlice.quests) ||
    oldSlice.quests.length === 0
  ) {
    return currentArchive ?? {};
  }
  const next = {
    ...(currentArchive ?? {}),
    [oldSlice.date]: {
      date: oldSlice.date,
      quests: oldSlice.quests,
    },
  };
  return pruneQuestArchive(next);
}

/**
 * Look up the archived slice for a given date, or null if missing.
 */
export function getArchivedSlice(archive, dateISO) {
  if (!archive || typeof archive !== 'object') return null;
  const entry = archive[dateISO];
  if (!entry || !Array.isArray(entry.quests)) return null;
  return entry;
}

/**
 * Synthesize a quest slice for a date the user wasn't around for.
 *
 * Delegates to `buildDailyQuestSlice`, which is deterministically seeded by
 * the date, so calling it for any past date yields the EXACT same quests the
 * app would have shown that day — including the Friday weekly-review mission
 * and the weekend rest (empty) days — modulo the dynamic mistakes_cleared
 * quest, which uses the current wrong-pool count rather than the (unknowable)
 * past count. That trade-off is fine: the make-up child gets a normal-looking
 * quest set, not whatever obscure thing they might have seen.
 *
 * `context` carries `{ availableWrongCount?, hasHistory? }` — see
 * `buildDailyQuestSlice`. `hasHistory` lets a missed Friday be made up with
 * its weekly-review mission.
 */
export function synthesizeQuestSlice(dateISO, context = {}) {
  return buildDailyQuestSlice(dateISO, context);
}

/**
 * Lazily populate the archive with synthesized slices for any date in
 * `targetDates` that isn't already archived. Used by `ensureDailyQuests`
 * so the calendar make-up flow works even when the child didn't open
 * the app on the missed day (the common case).
 *
 * Returns the same reference when nothing changed.
 */
export function populateArchiveForDates(
  currentArchive,
  targetDates,
  context = {}
) {
  if (!Array.isArray(targetDates) || targetDates.length === 0) {
    return currentArchive ?? {};
  }
  let next = currentArchive ?? {};
  let mutated = false;
  for (const dateISO of targetDates) {
    if (next[dateISO]) continue;
    const slice = synthesizeQuestSlice(dateISO, context);
    if (!slice.quests || slice.quests.length === 0) continue;
    if (!mutated) {
      next = { ...next };
      mutated = true;
    }
    next[dateISO] = slice;
  }
  return mutated ? pruneQuestArchive(next) : next;
}
